import { useCallback, useMemo } from 'react';
import { type EditCommand, applyMany, createEmptyEdl } from '@repo/editor-core';
import { EditorShell } from '@/editor/EditorShell';
import type { AgentChatApi } from '@/editor/AgentPanel';

/** Offline mock agent (?agent=mock) — streams a canned turn without the API. */
const MOCK_AGENT: AgentChatApi = {
  send: async (_messages, _edl, onEvent) => {
    onEvent({ type: 'thinking', text: '데모 모드예요. 하단에 자막을 하나 넣어볼게요.' });
    await new Promise((r) => setTimeout(r, 400));
    onEvent({ type: 'message', text: '"AI 데모" 자막을 앞부분에 넣었어요.' });
    onEvent({
      type: 'edit',
      command: {
        op: 'addCaption',
        trackId: 'caption-1',
        text: 'AI 데모',
        from: 0,
        durationInFrames: 45,
      },
    });
    await new Promise((r) => setTimeout(r, 200));
    onEvent({ type: 'done', applied: 1 });
  },
};

const CROP_W = (1080 * (9 / 16)) / 1920;

// Standalone sample: 2 clips + caption over the Phase 0 dev asset (/dev-asset.mp4).
const DEV_COMMANDS: EditCommand[] = [
  {
    op: 'addClip',
    trackId: 'video-1',
    source: { assetId: 'timecode', sourceInSec: 1, sourceOutSec: 4 },
    at: 0,
  },
  { op: 'setCrop', itemId: 'clip-1', crop: { x: (1 - CROP_W) / 2, y: 0, w: CROP_W, h: 1 } },
  {
    op: 'addClip',
    trackId: 'video-1',
    source: { assetId: 'timecode', sourceInSec: 8, sourceOutSec: 12 },
    at: 90,
  },
  { op: 'setCrop', itemId: 'clip-2', crop: { x: 1 - CROP_W, y: 0, w: CROP_W, h: 1 } },
  {
    op: 'addTransition',
    afterItemId: 'clip-1',
    beforeItemId: 'clip-2',
    kind: 'fade',
    durationInFrames: 30,
  },
  {
    op: 'addClip',
    trackId: 'broll-1',
    source: { assetId: 'timecode', sourceInSec: 14, sourceOutSec: 16 },
    at: 30,
    rect: { x: 0.6, y: 0.05, w: 0.35, h: 0.2 },
  },
  {
    op: 'addCaption',
    trackId: 'caption-1',
    text: '하이라이트',
    from: 30,
    durationInFrames: 150,
    style: { fontSizePx: 84, primaryColor: '#FFE600', alignment: 'bottom', marginVPx: 240 },
  },
  { op: 'setTitle', text: '오늘의 하이라이트 모음' },
];

/** Offline editor for verification — no API/auth. Needs `spike:gen-asset` first. */
export default function DevEditorPage() {
  const initialEdl = useMemo(() => applyMany(createEmptyEdl(), DEV_COMMANDS), []);
  const resolver = useCallback(() => '/dev-asset.mp4', []);
  const mockAgent =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('agent') === 'mock';
  return (
    <EditorShell
      initialEdl={initialEdl}
      resolver={resolver}
      title="쇼츠 에디터 — 개발용 샘플 (standalone)"
      agent={mockAgent ? MOCK_AGENT : undefined}
    />
  );
}
